import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  TrendingUp, TrendingDown, DollarSign, Banknote, CreditCard, 
  ArrowLeftRight, Scissors, Package, Calendar as CalendarIcon,
  Wallet, Users, ArrowUpRight, ArrowDownRight, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReports() {
  const { user, getAuthHeaders } = useAuth();
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 7),
    to: new Date()
  });
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [salesSummary, setSalesSummary] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [monthlyBalance, setMonthlyBalance] = useState(null);
  const [pendingCommissions, setPendingCommissions] = useState(null);
  const [paidCommissions, setPaidCommissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchReports();
    fetchMonthlyData();
  }, [dateRange, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      let availableBranches = response.data;
      
      // Manager and Admin can only see their assigned branch
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        availableBranches = response.data.filter(b => b.id === user.branch_id);
        setSelectedBranch(user.branch_id);
      }
      
      setBranches(availableBranches);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchMonthlyData = async () => {
    try {
      const branchParam = selectedBranch !== 'all' ? `?branch_id=${selectedBranch}` : '';
      const [balanceRes, commissionsRes, paidRes] = await Promise.all([
        axios.get(`${API}/reports/monthly-balance${branchParam}`, getAuthHeaders()),
        axios.get(`${API}/barbers/commissions/pending${branchParam}`, getAuthHeaders()),
        axios.get(`${API}/barbers/commissions/paid${branchParam}`, getAuthHeaders()),
      ]);
      setMonthlyBalance(balanceRes.data);
      setPendingCommissions(commissionsRes.data);
      setPaidCommissions(paidRes.data);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const startDate = format(startOfDay(dateRange.from), 'yyyy-MM-dd');
      const endDate = format(endOfDay(dateRange.to), 'yyyy-MM-dd');
      
      const [summaryRes, topRes] = await Promise.all([
        axios.get(`${API}/reports/sales-summary?start_date=${startDate}&end_date=${endDate}${branchParam}`, getAuthHeaders()),
        axios.get(`${API}/reports/top-products?start_date=${startDate}&end_date=${endDate}${branchParam}`, getAuthHeaders()),
      ]);
      setSalesSummary(summaryRes.data);
      setTopProducts(topRes.data);
    } catch (error) {
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = () => {
    if (!dateRange.from) return 'Seleccionar fechas';
    if (!dateRange.to) return format(dateRange.from, 'dd MMM yyyy', { locale: es });
    return `${format(dateRange.from, 'dd MMM', { locale: es })} - ${format(dateRange.to, 'dd MMM yyyy', { locale: es })}`;
  };

  const handleDateSelect = (range) => {
    if (range?.from) {
      setDateRange({
        from: range.from,
        to: range.to || range.from
      });
      if (range.to) {
        setCalendarOpen(false);
      }
    }
  };

  // Quick date range presets
  const setQuickRange = (days) => {
    setDateRange({
      from: subDays(new Date(), days),
      to: new Date()
    });
    setCalendarOpen(false);
  };

  const StatCard = ({ title, value, icon: Icon, color = 'gold', subtitle, testId }) => (
    <Card data-testid={testId} className="bg-zinc-900/50 border-zinc-800 p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">{title}</p>
          <p className={`font-heading text-2xl ${
            color === 'gold' ? 'text-gold' : 
            color === 'green' ? 'text-green-500' : 
            color === 'red' ? 'text-red-500' : 
            'text-white'
          }`}>
            {value}
          </p>
          {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-sm ${
          color === 'gold' ? 'bg-gold/10' : 
          color === 'green' ? 'bg-green-500/10' : 
          color === 'red' ? 'bg-red-500/10' : 
          'bg-zinc-800'
        } flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${
            color === 'gold' ? 'text-gold' : 
            color === 'green' ? 'text-green-500' : 
            color === 'red' ? 'text-red-500' : 
            'text-zinc-400'
          }`} />
        </div>
      </div>
    </Card>
  );

  const TopItem = ({ item, index }) => (
    <div className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
      <div className="flex items-center gap-3">
        <span className="w-6 h-6 rounded-sm bg-zinc-800 flex items-center justify-center text-xs text-zinc-400">
          {index + 1}
        </span>
        <span className="text-white">{item.name}</span>
      </div>
      <div className="text-right">
        <p className="text-gold font-semibold">${item.revenue.toFixed(2)}</p>
        <p className="text-xs text-zinc-500">{item.count} vendidos</p>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-reports">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Reportes</h1>
            <p className="text-zinc-500 mt-1">Análisis de ventas, comisiones y balance</p>
          </div>
          <div className="flex gap-3">
            <Select 
              value={selectedBranch} 
              onValueChange={setSelectedBranch}
              disabled={!isMasterAdmin && user?.branch_id}
            >
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-white">
                <SelectValue placeholder="Sucursal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {isMasterAdmin && (
                  <SelectItem value="all" className="text-white">Todas las sucursales</SelectItem>
                )}
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id} className="text-white">
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Date Range Picker */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[260px] justify-start text-left font-normal bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800 hover:text-white"
                  data-testid="date-range-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                  {formatDateRange()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="end">
                {/* Quick presets */}
                <div className="flex gap-2 p-3 border-b border-zinc-800">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuickRange(0)}
                    className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                  >
                    Hoy
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuickRange(7)}
                    className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                  >
                    7 días
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuickRange(30)}
                    className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                  >
                    30 días
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuickRange(90)}
                    className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                  >
                    90 días
                  </Button>
                </div>
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange}
                  onSelect={handleDateSelect}
                  numberOfMonths={2}
                  locale={es}
                  className="text-white"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center text-white",
                    caption_label: "text-sm font-medium text-white",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white border border-zinc-700 rounded-sm hover:bg-zinc-800",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-zinc-500 rounded-md w-8 font-normal text-[0.8rem]",
                    row: "flex w-full mt-2",
                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-gold/20 [&:has([aria-selected].day-outside)]:bg-gold/10 [&:has([aria-selected].day-range-end)]:rounded-r-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
                    day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 text-white hover:bg-zinc-800 rounded-sm",
                    day_range_start: "day-range-start bg-gold text-black hover:bg-gold hover:text-black",
                    day_range_end: "day-range-end bg-gold text-black hover:bg-gold hover:text-black",
                    day_selected: "bg-gold text-black hover:bg-gold hover:text-black focus:bg-gold focus:text-black",
                    day_today: "bg-zinc-800 text-gold",
                    day_outside: "day-outside text-zinc-600 aria-selected:bg-gold/20 aria-selected:text-zinc-400",
                    day_disabled: "text-zinc-700 opacity-50",
                    day_range_middle: "aria-selected:bg-gold/20 aria-selected:text-white",
                    day_hidden: "invisible",
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Monthly Balance Summary */}
        {monthlyBalance && (
          <Card className="bg-gradient-to-r from-zinc-900 via-zinc-900/80 to-zinc-900 border-zinc-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-4 h-4 text-gold" />
              <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                Balance del Mes - {monthlyBalance.month_name}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-green-500/10 rounded-sm border border-green-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpRight className="w-3 h-3 text-green-500" />
                  <p className="text-xs text-green-500 uppercase">Ingresos</p>
                </div>
                <p className="font-heading text-2xl text-green-500">
                  ${monthlyBalance.income?.total?.toFixed(2) || '0.00'}
                </p>
                {/* Desglose de ingresos */}
                <div className="mt-3 pt-3 border-t border-green-500/20 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Servicios</span>
                    <span className="text-zinc-300">${monthlyBalance.income?.services?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Productos</span>
                    <span className="text-zinc-300">${monthlyBalance.income?.products?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Transacciones</span>
                    <span className="text-zinc-300">{monthlyBalance.income?.transactions_count || 0}</span>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-red-500/10 rounded-sm border border-red-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowDownRight className="w-3 h-3 text-red-500" />
                  <p className="text-xs text-red-500 uppercase">Egresos</p>
                </div>
                <p className="font-heading text-2xl text-red-500">
                  ${monthlyBalance.expenses?.total?.toFixed(2) || '0.00'}
                </p>
                {/* Desglose de egresos */}
                <div className="mt-3 pt-3 border-t border-red-500/20 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Comisiones Pend.</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.commissions_pending?.toFixed(2) || '0.00'}</span>
                  </div>
                  {/* Sub-desglose de comisiones pendientes */}
                  <div className="pl-3 space-y-1 border-l border-zinc-700/50">
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">• Servicios</span>
                      <span className="text-zinc-400">${monthlyBalance.expenses?.commissions_services?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">• Propinas</span>
                      <span className="text-zinc-400">${monthlyBalance.expenses?.commissions_tips?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Comisiones Pag.</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.commissions_paid?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Gastos Fijos</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.fixed_expenses?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Costo Productos</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.product_costs?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Otros Gastos</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.other?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>
              <div className={`p-4 rounded-sm border ${monthlyBalance.balance >= 0 ? 'bg-gold/10 border-gold/20' : 'bg-red-500/10 border-red-500/20'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className={`w-3 h-3 ${monthlyBalance.balance >= 0 ? 'text-gold' : 'text-red-500'}`} />
                  <p className={`text-xs uppercase ${monthlyBalance.balance >= 0 ? 'text-gold' : 'text-red-500'}`}>Balance</p>
                </div>
                <p className={`font-heading text-2xl ${monthlyBalance.balance >= 0 ? 'text-gold' : 'text-red-500'}`}>
                  ${monthlyBalance.balance?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-4 bg-zinc-800/50 rounded-sm border border-zinc-700">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-3 h-3 text-zinc-400" />
                  <p className="text-xs text-zinc-500 uppercase">Margen</p>
                </div>
                <p className={`font-heading text-2xl ${monthlyBalance.profit_margin >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {monthlyBalance.profit_margin?.toFixed(1) || '0'}%
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Commissions Summary */}
        {pendingCommissions && (
          <Card className="bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gold" />
                <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                  Comisiones por Pagar
                </h3>
              </div>
              <span className="text-2xl font-heading text-green-500">
                ${pendingCommissions.total_pending?.toFixed(2) || '0.00'}
              </span>
            </div>
            {pendingCommissions.by_barber?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingCommissions.by_barber.filter(b => b.pending_commission > 0).map((barber) => (
                  <div key={barber.barber_id} className="p-3 bg-zinc-800/50 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">{barber.barber_name}</p>
                      <span className="text-green-500 font-semibold">${barber.pending_commission.toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-zinc-500 space-y-0.5">
                      <div className="flex justify-between">
                        <span>{barber.services_count} servicios</span>
                        <span className="text-zinc-400">${barber.services_commission?.toFixed(2) || '0.00'}</span>
                      </div>
                      {barber.tips_count > 0 && (
                        <div className="flex justify-between">
                          <span>{barber.tips_count} propinas</span>
                          <span className="text-zinc-400">${barber.tips_total?.toFixed(2) || '0.00'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-600 text-center py-4">No hay comisiones pendientes</p>
            )}
          </Card>
        )}

        {/* Paid Commissions */}
        {paidCommissions && (
          <Card className="bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                  Comisiones Pagadas (Este Mes)
                </h3>
              </div>
              <span className="text-2xl font-heading text-emerald-500">
                ${paidCommissions.total_paid?.toFixed(2) || '0.00'}
              </span>
            </div>
            {paidCommissions.by_barber?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {paidCommissions.by_barber.filter(b => b.paid_commission > 0).map((barber) => (
                  <div key={barber.barber_id} className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-sm flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{barber.barber_name}</p>
                      <p className="text-xs text-zinc-500">{barber.payments_count} pago{barber.payments_count !== 1 ? 's' : ''}</p>
                    </div>
                    <span className="text-emerald-500 font-semibold">${barber.paid_commission.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-600 text-center py-4">No hay comisiones pagadas este mes</p>
            )}
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Total Ventas" 
                value={`$${salesSummary?.total_sales?.toFixed(2) || '0.00'}`}
                icon={DollarSign}
                subtitle={`${salesSummary?.transactions_count || 0} transacciones`}
              />
              <StatCard 
                title="Efectivo" 
                value={`$${salesSummary?.by_payment_method?.cash?.toFixed(2) || '0.00'}`}
                icon={Banknote}
                color="white"
              />
              <StatCard 
                title="Tarjeta" 
                value={`$${salesSummary?.by_payment_method?.card?.toFixed(2) || '0.00'}`}
                icon={CreditCard}
                color="white"
              />
              <StatCard 
                title="Transferencia" 
                value={`$${salesSummary?.by_payment_method?.transfer?.toFixed(2) || '0.00'}`}
                icon={ArrowLeftRight}
                color="white"
              />
            </div>

            {/* Gastos + Nóminas del rango seleccionado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard
                title="Gastos"
                value={`$${salesSummary?.expenses_total?.toFixed(2) || '0.00'}`}
                icon={TrendingDown}
                color="red"
                subtitle="Gastos fijos del periodo"
                testId="admin-reports-expenses-card"
              />
              <StatCard
                title="Nóminas / Comisiones Pagadas"
                value={`$${salesSummary?.payroll_total?.toFixed(2) || '0.00'}`}
                icon={Wallet}
                color="gold"
                subtitle={`Comisiones $${salesSummary?.commissions_paid?.toFixed(2) || '0.00'} · Propinas $${salesSummary?.tips_paid?.toFixed(2) || '0.00'}`}
                testId="admin-reports-payroll-card"
              />
            </div>

            {/* Chart */}
            {salesSummary?.daily_breakdown?.length > 0 && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Ventas por día - {formatDateRange()}
                  </h3>
                  <CalendarIcon className="w-4 h-4 text-zinc-600" />
                </div>
                <div className="flex items-end gap-2 h-48">
                  {salesSummary.daily_breakdown.map((day, index) => {
                    const maxValue = Math.max(...salesSummary.daily_breakdown.map(d => d.total));
                    const height = maxValue > 0 ? (day.total / maxValue) * 100 : 0;
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center gap-2">
                        <span className="text-xs text-gold">${day.total.toFixed(0)}</span>
                        <div 
                          className="w-full bg-gold/80 rounded-sm transition-all hover:bg-gold"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                        <span className="text-xs text-zinc-500">{day.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Top Products/Services */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Scissors className="w-4 h-4 text-gold" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Servicios más vendidos
                  </h3>
                </div>
                {topProducts?.top_services?.length > 0 ? (
                  <div>
                    {topProducts.top_services.map((item, index) => (
                      <TopItem key={item.name} item={item} index={index} />
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-600 text-center py-8">Sin datos</p>
                )}
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-4 h-4 text-gold" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Productos más vendidos
                  </h3>
                </div>
                {topProducts?.top_products?.length > 0 ? (
                  <div>
                    {topProducts.top_products.map((item, index) => (
                      <TopItem key={item.name} item={item} index={index} />
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-600 text-center py-8">Sin datos</p>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
